import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { StateService } from '../../../services/state/stateService';
import { State } from '../../../type/entities/state-type';

/*----------------------------------------------------------------------------------------
* EN: Hook to manage Contract products states fetching and transformation logic.
* ES: Hook para gestionar la lógica de obtención y transformación de estados de prductos para contratos.
----------------------------------------------------------------------------------------*/

const useContractProductsStates = (initialFilters = {}) => {

    // HOOKS

    const { user } = useSelector((state: RootState) => state.auth);

    // STATES

    const [data, setData] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [filters, setFilters] = useState<Record<string, any>>({
        limit: 999999,
        filter_filters: { ...initialFilters }
    });

    // FUNCTIONS

    // --------------------------------------------------------------
    /*
    * EN: Function to get the contract products states list.
    * ES: Función para obtener la lista de estados de productos en contratos.
    */
    // --------------------------------------------------------------
    const fetchContractProductsStates = async () => {
        setIsLoading(true);
        try {
            const service = new StateService();
            let requestFilters = { ...filters };

            // IF USER IS NOT LINKED TO A DEPARTMENT

            if (user?.id) {
                if (!user.department) {
                    requestFilters = {
                        ...requestFilters,
                        filter_filters: {
                            ...requestFilters.filter_filters,
                            ignore_department_filter: true
                        }
                    };
                }
            }

            const response = await service.listStatusesForSelect(requestFilters);
            const fetchedData = response.getResponseData()?.data;

            if (fetchedData && fetchedData?.data) {
                const mappedData = Object.values(
                    fetchedData.data.reduce((acc: any, state: State) => {
                        if (!acc[state.id]) {
                            acc[state.id] = {
                                value: state.id,
                                label: state.name,
                                color: state.color,
                                statusOrder: state.statusOrder,
                                initialStatus: state.initialStatus,
                                unsatisfactoryStatus: state.unsatisfactoryStatus,
                                requiresKoReason: state.requiresKoReason,
                                productTypes: [],
                            };
                        }

                        if (state.productTypes?.productType?.id) {
                            acc[state.id].productTypes.push(state.productTypes.productType.id);
                        }

                        return acc;
                    }, {})
                );

                setData(mappedData);
            }
        } catch (error) {
            console.log('Error fetching contract products states:', error);
        } finally {
            setIsLoading(false);
        }
    };
    // --------------------------------------------------------------
    
    // --------------------------------------------------------------
    /*
    * EN: Function to get the contract products states list.
    * ES: Función para obtener la lista de estados de productos en contratos.
    */
    // --------------------------------------------------------------
    const getContractProductsStatesList = (entity?: string | null, notShowStatusID?: string | null) => {
        if (entity) return data.filter((state: any) => state.entity === entity);
        if (notShowStatusID) return data.filter((state: any) => state.value !== notShowStatusID);
        return data;
    };
    // --------------------------------------------------------------

    // --------------------------------------------------------------
    /*
    * EN: Function to add a filter to the contract products states list.
    * ES: Función para agregar un filtro a la lista de estados de productos en contratos.
    */
    // --------------------------------------------------------------
    const addContractProductsStatesFilter = (key: string, value: any) => {
        setFilters(prevFilters => ({
            ...prevFilters,
            filter_filters: {
                ...prevFilters.filter_filters,
                [key]: value
            }
        }));
    };
    // --------------------------------------------------------------


    // USE EFFECTS

    useEffect(() => {
        fetchContractProductsStates();
    }, [filters]);

    // RETURN

    return { fetchContractProductsStates, getContractProductsStatesList, addContractProductsStatesFilter, isLoadingContractProductsStates: isLoading };
}

export default useContractProductsStates;